import React, { useState, useRef, useEffect, useCallback } from "react";
import cycleBg from "../Img/cycle1.png";
import person1 from "../Img/person1.jpg";
import person2 from "../Img/person2.jpg";
import person3 from "../Img/person3.jpg";
import person4 from "../Img/person4.jpg";
import cycleDetailImage from "../Img/cycle-detail-image.png";
import contactFormBg from "../Img/contact-form-bg.png";
import tribeimage from "../Img/gallery.png";
import gearCycle from "../Img/cycle/gear.png";
import kidsCycle from "../Img/cycle/kids.png";
import nonGearCycle from "../Img/cycle/nongear.png";
import premiumCycle from "../Img/cycle/premium.png";
import {
  Bike,
  Package,
  Wind,
  RefreshCw,
  Bell,
  Shield,
  Video,
  Wrench,
  Repeat,
  PhoneCall,
  Star,
  Crown,
  Heart,
  Sparkles,
  Quote,
  ChevronLeft,
  ChevronRight,
  ArrowRight,
  Phone,
  Mail,
  User,
  Smartphone,
  MessageCircle,
} from "lucide-react";
import "../CSS/Global.css";
import "../CSS/Home.css";
import { Link } from "react-router-dom";

const Home = () => {
  const [startIndex, setStartIndex] = useState(0);

  const scrollRef = useRef(null);
  const rafRef = useRef(null);
  const isPaused = useRef(false);

  const scrollCycleRow = useCallback(() => {
    if (!scrollRef.current || isPaused.current) return;

    const container = scrollRef.current;
    let currentTranslate = 0;
    const speed = 0.3;
    const duration = 40; // ms per frame approx

    const animate = () => {
      currentTranslate -= speed;
      if (currentTranslate <= -container.offsetWidth) {
        currentTranslate = 0;
      }
      container.style.transform = `translateX(${currentTranslate}px)`;

      rafRef.current = requestAnimationFrame(animate);
    };

    animate();

    return () => {
      if (rafRef.current) cancelAnimationFrame(rafRef.current);
    };
  }, []);

  useEffect(() => {
    const cleanup = scrollCycleRow();
    return cleanup;
  }, [scrollCycleRow]);

  const handleMouseEnter = useCallback(() => {
    isPaused.current = true;
    if (rafRef.current) {
      cancelAnimationFrame(rafRef.current);
    }
  }, []);

  const handleMouseLeave = useCallback(() => {
    isPaused.current = false;
    scrollCycleRow();
  }, [scrollCycleRow]);

  const includes = [
    {
      icon: <Bell className="include-icon" />,
      title: "Fully Equipped Cycle",
      description:
        "Receive a well-maintained bicycle equipped with essentials like a bell, adjustable seat, bottle holder, mudguards, and seat cover for a smooth and comfortable ride.",
    },
    {
      icon: <Shield className="include-icon" />,
      title: "Safety Gear",
      description:
        "Every rental comes with a safety kit including a helmet, lock, reflectors, and tail light to ensure you stay safe while enjoying your ride.",
    },
    {
      icon: <Video className="include-icon" />,
      title: "Helpful Video Guides",
      description:
        "Access easy tutorial videos whenever you need help. Learn basics like gear shifting, fixing the chain, and maintaining proper tyre pressure.",
    },
    {
      icon: <Wrench className="include-icon" />,
      title: "3 Complimentary Services",
      description:
        "Enjoy up to three free servicing sessions during your rental period to keep your bicycle in excellent condition without any extra cost.",
    },
    {
      icon: <Repeat className="include-icon" />,
      title: "Flexible Cycle Exchange",
      description:
        "Switch your bicycle anytime. Upgrade or change between non-gear, geared, or premium cycles depending on your riding preference.",
    },
    {
      icon: <PhoneCall className="include-icon" />,
      title: "24/7 Customer Support",
      description:
        "Our support team is available day and night to assist you with any issues, ensuring your cycling experience remains smooth and worry-free.",
    },
  ];

  const pricingPlans = [
    {
      title: "NON GEAR",
      price: "599",
      gradient: "linear-gradient(135deg, #64748B, #475569)",
      popular: false,
    },
    {
      title: "GEAR",
      price: "899",
      gradient: "linear-gradient(135deg, var(--blue-1), var(--blue-2))",
      popular: true,
    },
    {
      title: "PREMIUM",
      price: "1,499",
      gradient: "linear-gradient(135deg, #F59E0B, #FBBF24)",
      popular: false,
    },
    {
      title: "KIDS",
      price: "249",
      gradient: "linear-gradient(135deg, #EC4899, #F472B6)",
      popular: false,
    },
  ];

  // Testimonials with EXACT text from your image
  const testimonials = [
    {
      name: "Vishal Desai",
      rating: 5,
      text: "I rented a bicycle from Cycle Rental Service last month and it was in excellent condition. The whole rental process was quick and very simple. Riding the cycle was a great experience and I will definitely rent again in the future. I highly recommend Cycle Rental Service to anyone looking for a reliable cycle rental.",
      image: person1,
    },
    {
      name: "Shraddha Aryaa",
      rating: 5,
      text: "Renting a cycle from Cycle Rental Service was a wonderful experience for me. After my second baby, I wanted to get back into a healthy routine and cycling helped a lot. The bicycles were comfortable and the team was extremely supportive. I truly appreciate their service and wish them continued success.",
      image: person2,
    },
    {
      name: "Alisha Pereira",
      rating: 5,
      text: "My experience with Cycle Rental Service was very smooth from the first enquiry to the delivery and pickup of the bicycle. I wanted to relearn cycling after many years and their team was very helpful. They even suggested the right cycle based on my height, which made the ride comfortable and enjoyable.",
      image: person3,
    },
    {
      name: "Binnita Shah",
      rating: 5,
      text: "I was searching online to rent cycles for myself and my brother when I found Cycle Rental Service. The team was very friendly and helped us even on short notice during a Sunday. They took the time to understand our plans and recommended the best cycles for us. It was a great experience overall.",
      image: person4,
    },
  ];

  const cycleTypesBase = [
    { name: "KIDS", image: kidsCycle },
    { name: "PREMIUM", image: premiumCycle },
    { name: "GEAR", image: gearCycle },
    { name: "NON GEAR", image: nonGearCycle },
  ];
  const cycleTypes = [...cycleTypesBase, ...cycleTypesBase, ...cycleTypesBase];

  const renderStars = (rating) => {
    return [...Array(5)].map((_, index) => (
      <Star
        key={index}
        size={16}
        className={index < rating ? "star-filled" : "star-empty"}
        fill={index < rating ? "currentColor" : "none"}
      />
    ));
  };

  const nextSlide = () => {
    if (startIndex + 3 < testimonials.length) {
      setStartIndex(startIndex + 1);
    }
  };

  const prevSlide = () => {
    if (startIndex > 0) {
      setStartIndex(startIndex - 1);
    }
  };

  return (
    <div className="min-h-screen">
      <section className="hero-section-complete">
        <div className="hero-bg-spot spot-1"></div>
        <div className="hero-bg-spot spot-2"></div>
        <div className="container">
          {/* Main Hero Content */}
          <div className="hero-main-content">
            <h1 className="hero-main-title">
              Your Reliable Bicycle Rental Companion
            </h1>
            <p className="hero-main-subtitle">
              Rent affordable and well-maintained bicycles easily
            </p>
            <Link to="/products" className="hero-main-btn">
              Book Your Cycle →
            </Link>
          </div>

          {/* Auto-scrolling Cycle Icons Row */}
          <div
            className="hero-cycle-row"
            ref={scrollRef}
            onMouseEnter={handleMouseEnter}
            onMouseLeave={handleMouseLeave}
          >
            {cycleTypes.map((cycle, index) => (
              <div key={index} className="hero-cycle-item">
                <img src={cycle.image} alt={cycle.name} />
                <span>{cycle.name}</span>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* New Hero Followup Section */}
      <section className="hero-followup-section">
        <div className="container">
          <div className="followup-content">
            <div className="followup-left">
              <h2 className="followup-title">Select Your Perfect Ride</h2>
              <p className="followup-subtitle">
                High-quality bicycles brought straight to you
              </p>
              <Link to="/products" className="followup-cta">
                Ride Today →
              </Link>
            </div>
            <div className="followup-right">
              <img
                src={cycleDetailImage}
                alt="Premium Cycle Detail"
                className="followup-image"
              />
            </div>
          </div>
        </div>
      </section>

      {/* How It Works Section */}
      <section className="how-it-works">
        <div className="container">
          <h2 className="section-title">How it Works</h2>

          <div className="steps">
            <div className="step">
              <Bike className="step-icon" />
              <div className="step-content">
                <h3>Choose Your Cycle</h3>
                <p>
                  Browse and pick the perfect bicycle from our wide range of
                  options
                </p>
              </div>
            </div>

            <div className="step">
              <Package className="step-icon" />
              <div className="step-content">
                <h3>Doorstep Delivery</h3>
                <p>
                  Your selected bicycle and safety gear will be delivered to
                  your location
                </p>
              </div>
            </div>

            <div className="step">
              <Wind className="step-icon" />
              <div className="step-content">
                <h3>Enjoy Your Ride</h3>
                <p>
                  Ride with confidence knowing support and service are always
                  available
                </p>
              </div>
            </div>

            <div className="step">
              <RefreshCw className="step-icon" />
              <div className="step-content">
                <h3>Extend or Return</h3>
                <p>
                  Renew your rental, switch cycles, or return it whenever you're
                  ready
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="cta-section">
        <div className="container">
          <h2 className="section-title-two-same">Ready to Start Your Ride?</h2>
          <p className="cta-subtitle">
            Choose from 350+ premium cycles. Home delivery in 24 hours.
          </p>
          <Link to="/products" className="cta-button">
            Rent a Cycle Now →
          </Link>
        </div>
      </section>

      {/* Includes Section */}
      <section className="includes-section">
        <div className="container">
          <h2 className="section-title">What's Included</h2>

          <div className="includes-grid">
            {includes.map((item, index) => (
              <div className="include-card" key={index}>
                <div className="include-icon-wrapper">{item.icon}</div>
                <h3>{item.title}</h3>
                <p>{item.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Pricing Section */}
      <section className="pricing-section-simple">
        <div className="container">
          <h2 className="section-title">Choose Your Rental Plan</h2>
          <p className="section-subtitle">
            Choose the perfect plan for your riding needs
          </p>

          <div className="pricing-grid-simple">
            {pricingPlans.map((plan, index) => (
              <div
                className={`pricing-card-simple ${plan.popular ? "popular" : ""}`}
                key={index}
              >
                {plan.popular && (
                  <div className="popular-badge-simple">
                    <Star size={14} />
                    <span>Most Popular</span>
                  </div>
                )}

                <h3>{plan.title}</h3>

                <div className="price-container-simple">
                  <span className="currency">Rs</span>
                  <span className="price">{plan.price}</span>
                  <span className="period">/month*</span>
                </div>

                <button
                  className="choose-plan-btn-simple"
                  style={{ background: plan.gradient }}
                >
                  Choose
                </button>

                <p className="pricing-note-simple">
                  *Prices mentioned above are for the yearly cycle rental
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="cta-section-two-same">
        <div className="container">
          <h2 className="section-title-two-same">
            Don’t ignore your fitness, start cycling today
          </h2>
          <p className="cta-subtitle-two-same">
            Rent a bicycle and take the first step toward a healthier lifestyle
          </p>
          <Link to="/products" className="cta-button-two-same">
            Start Riding →
          </Link>
        </div>
      </section>

      {/* Testimonials Carousel - 3 Cards at a Time */}
      <section className="testimonials-carousel-section">
        <div className="container">
          <h2 className="section-title">What Our Riders Say</h2>

          <div className="testimonials-carousel-container">
            <button
              className={`carousel-arrow carousel-arrow-left ${startIndex === 0 ? "disabled" : ""}`}
              onClick={prevSlide}
              disabled={startIndex === 0}
            >
              <ChevronLeft size={24} />
            </button>

            <div className="testimonials-carousel-track">
              {testimonials
                .slice(startIndex, startIndex + 3)
                .map((testimonial, index) => (
                  <div
                    key={startIndex + index}
                    className="testimonial-carousel-card"
                  >
                    <div className="testimonial-header">
                      <img
                        src={testimonial.image}
                        alt={testimonial.name}
                        className="testimonial-image"
                      />
                      <div className="testimonial-info">
                        <h4>{testimonial.name}</h4>
                        <div className="testimonial-rating">
                          {renderStars(testimonial.rating)}
                        </div>
                      </div>
                    </div>

                    <div className="testimonial-content">
                      <p>"{testimonial.text}"</p>
                    </div>
                  </div>
                ))}
            </div>

            <button
              className={`carousel-arrow carousel-arrow-right ${startIndex + 3 >= testimonials.length ? "disabled" : ""}`}
              onClick={nextSlide}
              disabled={startIndex + 3 >= testimonials.length}
            >
              <ChevronRight size={24} />
            </button>
          </div>

          <div className="carousel-dots">
            {Array.from({ length: Math.ceil(testimonials.length - 2) }).map(
              (_, index) => (
                <button
                  key={index}
                  className={`carousel-dot ${index === startIndex ? "active" : ""}`}
                  onClick={() => setStartIndex(index)}
                />
              ),
            )}
          </div>
        </div>
      </section>

      {/* Join the Tribe Section */}
      <section className="join-tribe-section">
        <div className="container">
          <div className="join-tribe-content">
            <h2 className="tribe-section-title">Join Our Community</h2>
            <img
              src={tribeimage}
              alt="Join the Tribe Gallery"
              className="join-tribe-image"
            />
          </div>
        </div>
      </section>

      <section className="home-section">
        <div className="home-overlay"></div>
        <div className="container">
          <div className="home-content">
            <h2 className="home-title">Here To Support You</h2>

            <div className="home-contact">
              <div className="home-contact-item">
                <Phone size={20} />
                <a href="tel:+919820760684">+91 9820760684</a>
              </div>
              <div className="home-contact-item">
                <Mail size={20} />
                <a href="mailto:hello@twinring.in">hello@twinring.in</a>
              </div>
            </div>

            <form className="home-form">
              <div className="form-group">
                <User size={18} className="form-icon" />
                <input type="text" placeholder="Full Name" />
              </div>

              <div className="form-group">
                <Smartphone size={18} className="form-icon" />
                <input type="tel" placeholder="Mobile Number" />
              </div>

              <div className="form-group">
                <Mail size={18} className="form-icon" />
                <input type="email" placeholder="Email" />
              </div>

              <div className="form-group">
                <MessageCircle size={18} className="form-icon" />
                <input type="text" placeholder="What are you looking for?" />
              </div>

              <button type="submit" className="home-submit-btn">
                Submit
              </button>
            </form>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Home;
